import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { DialogTitleWithClose } from '../../common';
import { adjustOpacity } from '../../../theme/utils';
import {
  Science as ExperimentIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useExperiments } from '../hooks/useExperiments';
import { useTemplates } from '../hooks/useTemplates';
import { validateExperimentData } from '../utils/helpers';
import { useContentCheckAssignment } from '../../ContentCheckPolicyManager/hooks/useContentCheckAssignment';
import {
  REQUEST_TIMEOUT_LONG_OPERATION,
  EXPERIMENT_MIN_SAMPLE_SIZE,
  EXPERIMENT_DEFAULT_STATISTICAL_SIGNIFICANCE,
} from '../../../config/constants';
import {
  TemplateInfoCard,
  ExperimentDetailsForm,
  TreatmentsSection,
} from './ExperimentCreationStepComponents';
import { useExperimentCreationForm } from './ExperimentCreationStepComponents/useExperimentCreationForm';

const ExperimentCreationStep = ({
  open,
  onClose,
  onSubmit,
  selectedTemplate,
  onBack,
}) => {
  const theme = useTheme();
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    isDescriptionExpanded,
    toggleDescriptionExpansion,
    shouldShowMoreButton,
    handleInputChange,
    handleTreatmentChange,
    removeTreatment,
    initializeForm,
  } = useExperimentCreationForm(selectedTemplate);

  const [submitting, setSubmitting] = useState(false);

  const { getTrafficAllocationStrategies } = useExperiments();

  const {
    getTemplate,
    getTemplateVersions,
    loading: templateLoading,
  } = useTemplates();

  // Load security policies for the template (for tooltip)
  const { assignments: securityPolicies } = useContentCheckAssignment(
    selectedTemplate?.id || selectedTemplate?.template_id || null
  );

  const [templateVersions, setTemplateVersions] = useState([]);
  const [trafficStrategies, setTrafficStrategies] = useState([]);
  const [longLoading, setLongLoading] = useState(false);

  // Safe traffic allocation value that matches available strategies
  const safeTrafficAllocation = useMemo(() => {
    return trafficStrategies.some(s => s.name === formData.traffic_allocation)
      ? formData.traffic_allocation
      : '';
  }, [formData.traffic_allocation, trafficStrategies]);

  // Helper function to get safe template version value
  const getSafeTemplateVersion = useCallback(
    treatmentVersion => {
      if (!treatmentVersion && treatmentVersion !== 0) return '';
      const versionNum =
        typeof treatmentVersion === 'number'
          ? treatmentVersion
          : parseInt(treatmentVersion);
      if (isNaN(versionNum)) return '';
      return templateVersions.some(v => parseInt(v.version) === versionNum)
        ? versionNum
        : '';
    },
    [templateVersions]
  );

  const loadTemplateDetails = useCallback(
    async templateId => {
      try {
        await getTemplate(templateId);
      } catch (error) {
        // Error loading template details
      }
    },
    [getTemplate]
  );

  const loadTemplateVersions = useCallback(
    async templateId => {
      try {
        const versionsData = await getTemplateVersions(templateId);
        const versions = versionsData.templates || [];
        setTemplateVersions(versions);
      } catch (error) {
        // If versions fail to load, just use the current template as the only version
        setTemplateVersions([selectedTemplate]);
      }
    },
    [getTemplateVersions, selectedTemplate]
  );

  const loadTrafficStrategies = useCallback(async () => {
    try {
      const strategiesData = await getTrafficAllocationStrategies();
      setTrafficStrategies(strategiesData.strategies || []);
    } catch (error) {
      // Fallback to default strategies if API fails
      setTrafficStrategies([
        {
          name: 'equal_split',
          description: 'Equal traffic split between all treatments',
        },
        {
          name: 'weighted_split',
          description: 'Custom weighted traffic allocation',
        },
        {
          name: 'single_treatment',
          description: '100% traffic to single treatment',
        },
      ]);
    }
  }, [getTrafficAllocationStrategies]);

  // Initialize form data when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      initializeForm();
      loadTemplateDetails(selectedTemplate.id);
      loadTemplateVersions(selectedTemplate.id);
      loadTrafficStrategies();
    }
  }, [
    selectedTemplate,
    initializeForm,
    loadTemplateDetails,
    loadTemplateVersions,
    loadTrafficStrategies,
  ]);

  // Long-loading notice (>=60s) only if actually loading
  useEffect(() => {
    if (!open || !templateLoading) {
      setLongLoading(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (templateLoading) {
        setLongLoading(true);
      }
    }, REQUEST_TIMEOUT_LONG_OPERATION);
    return () => clearTimeout(timer);
  }, [open, templateLoading]);

  const addTreatment = () => {
    // Find the next available version for this treatment
    const usedVersions = formData.treatments.map(t => t.template_version);
    const availableVersions = templateVersions.filter(
      v => !usedVersions.includes(parseInt(v.version))
    );
    const nextVersion =
      availableVersions.length > 0
        ? parseInt(availableVersions[0].version)
        : parseInt(selectedTemplate.version);

    const newTreatment = {
      treatment_id: `variant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `Variant ${formData.treatments.length}`,
      description: `Variant ${formData.treatments.length} treatment`,
      treatment_type: 'treatment',
      template_version: nextVersion,
      template_modifications: {},
      hypothesis: '',
      tags: [],
    };

    setFormData(prev => ({
      ...prev,
      treatments: [...prev.treatments, newTreatment],
    }));
  };

  const validateForm = () => {
    const validation = validateExperimentData(formData);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      // Error creating experiment
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      template_id: '',
      treatments: [],
      traffic_allocation: 'equal_split',
      success_metrics: [],
      minimum_sample_size: EXPERIMENT_MIN_SAMPLE_SIZE,
      statistical_significance: EXPERIMENT_DEFAULT_STATISTICAL_SIGNIFICANCE,
      target_audience: null,
      exclusion_criteria: null,
      metadata: {},
    });
    setErrors({});
    onClose();
  };

  if (!selectedTemplate) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: 'auto',
        },
      }}
    >
      <DialogTitleWithClose
        title={
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
              <ExperimentIcon
                sx={{ fontSize: 20, color: theme.palette.primary.main }}
              />
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  fontSize: '1rem',
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                }}
              >
                Step 2: Create Experiment
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                pl: 3,
                lineHeight: 1.4,
                color: theme.palette.text.secondary,
                fontSize: '0.8125rem',
              }}
            >
              Configure your A/B test using the selected template:{' '}
              <strong style={{ color: theme.palette.text.primary }}>
                {selectedTemplate.name}
              </strong>
            </Typography>
          </Box>
        }
        onClose={handleClose}
        disableClose={submitting}
        sx={{
          pb: 1.5,
          pt: 2,
          px: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      />

      <DialogContent
        sx={{
          minHeight: 400,
          maxHeight: 'calc(90vh - 200px)',
          overflowY: 'auto',
          px: 3,
          pt: 2,
          pb: 2,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TemplateInfoCard
              selectedTemplate={selectedTemplate}
              templateVersions={templateVersions}
              templateLoading={templateLoading}
              longLoading={longLoading}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ExperimentDetailsForm
              formData={formData}
              errors={errors}
              onInputChange={handleInputChange}
              isDescriptionExpanded={isDescriptionExpanded}
              onToggleDescription={toggleDescriptionExpansion}
              shouldShowMoreButton={shouldShowMoreButton}
              trafficStrategies={trafficStrategies}
              safeTrafficAllocation={safeTrafficAllocation}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TreatmentsSection
              treatments={formData.treatments}
              errors={errors}
              templateVersions={templateVersions}
              onTreatmentChange={handleTreatmentChange}
              onAddTreatment={addTreatment}
              onRemoveTreatment={removeTreatment}
              getSafeTemplateVersion={getSafeTemplateVersion}
              securityPolicies={securityPolicies}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Button
          onClick={onBack}
          startIcon={<BackIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor:
                theme.palette.action.hover || theme.palette.background.default,
            },
          }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor:
                theme.palette.action.hover || theme.palette.background.default,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress
                size={20}
                sx={{ color: theme.palette.primary.contrastText }}
              />
            ) : (
              <ExperimentIcon />
            )
          }
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 1.5,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: !submitting
              ? `0 2px 8px ${adjustOpacity(theme.palette.primary.main, 0.3)}`
              : 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: !submitting
                ? `0 4px 12px ${adjustOpacity(theme.palette.primary.main, 0.4)}`
                : 'none',
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}
        >
          {submitting ? 'Creating...' : 'Create Experiment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExperimentCreationStep;
